'use strict';

import _ from 'lodash';
import moment from 'moment';
import { EventEmitter } from 'events';
import AppDispatcher from '../dispatcher/AppDispatcher';
import HotelConstants from '../constants/HotelConstants';
import { FIREBASE_STRUCTURE } from 'arbitrip-common/general/constants';
import FirebaseManager from 'arbitrip-common/client/utils/Firebase/Manager';
import DealUtils from 'arbitrip-common/client/entities/DealUtils';
import ResultsStore from './ResultsStore';
import HotelActions from '../actions/HotelActions';

const STATUS = {
    IDLE: 'idle',
    BUSY: 'busy',
    SUCCESS: 'success',
    NO_AVAILABILITY: 'no_availability',
};

const CATERING_ROOM_ONLY = 'Room Only';

const deadline_display_key = (deadline) =>
    deadline ? moment.utc(deadline).subtract(1, 'day').endOf('day').valueOf() : null;

let _status = STATUS.IDLE;
let _clicked_deal = null;
let _hotel_id = null;
let _lazy_deals = [];
let _lazy_deal_ids = [];
let _raw_recheck_deals = [];
let _deals_to_ban = [];
let _navigation_deal = null;
let _price_change_data = null;
let _listener_id = null;
let _is_expired = false;

function _reset() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
    }
    _status = STATUS.IDLE;
    _clicked_deal = null;
    _hotel_id = null;
    _lazy_deals = [];
    _lazy_deal_ids = [];
    _raw_recheck_deals = [];
    _deals_to_ban = [];
    _navigation_deal = null;
    _price_change_data = null;
    _listener_id = null;
    _is_expired = false;
}

function _onFirebaseData(rechecks) {
    if (!rechecks) return;

    const deals = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.FIELD, []);
    const messages = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE, []);

    if (Array.isArray(deals) && deals.length > 0) {
        deals.forEach((raw_deal) => {
            AppDispatcher.handleAction({
                actionType: HotelConstants.LAZY_RECHECK_DEAL_RECEIVED,
                data: { raw_deal },
            });
        });
    }

    const recheck_completed =
        Array.isArray(messages) &&
        messages.filter((msg) => msg === FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE_RECHECK_COMPLETED);
    if (recheck_completed && recheck_completed.length > 0) {
        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_COMPLETED,
            data: {},
        });
    }
}

function _onFirebaseError(error) {
    console.error('LazyRecheckStore: Firebase error', error);
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: {},
    });
}

function _onFirebaseTimeout() {
    console.log('_onFirebaseTimeout');
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: {},
    });
}

function _banDeals(hotel, deals) {
    if (!hotel || !deals.length) return;
    const snapshot = deals.slice();
    setTimeout(() => snapshot.forEach((deal) => HotelActions.banDeal(hotel, { ...deal, hide: true })), 0);
}

function _handleCompleted() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
        _listener_id = null;
    }
    if (_status !== STATUS.BUSY) {
        return;
    }

    const hotel = ResultsStore.getHotelById(_hotel_id);

    if (_raw_recheck_deals.length === 0) {
        _deals_to_ban = _lazy_deals.slice();
        _banDeals(hotel, _deals_to_ban);
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    if (hotel) {
        hotel.addRawRecheckedDeals(ResultsStore.getCurrentSearchSession(), _raw_recheck_deals);
    }

    const returned_ids = new Set(_raw_recheck_deals.map((d) => d.id));
    _deals_to_ban = _lazy_deals.filter((d) => !returned_ids.has(d.id));
    const deals_to_ban_ids = _deals_to_ban.map((d) => d.id);
    _banDeals(hotel, _deals_to_ban);

    const hotel_deals = (hotel && hotel.deals) || [];
    const available_deals = hotel_deals.filter(
        (d) =>
            returned_ids.has(d.id) &&
            d.recheck_id &&
            !deals_to_ban_ids.includes(d.id) &&
            !d.is_lazy_recheck,
    );
    console.log('available_deals', available_deals);
    if (available_deals.length === 0) {
        // Returned-but-faulty deals weren't caught by the not-returned filter; ban them too.
        const faulty_returned = _lazy_deals.filter((d) => returned_ids.has(d.id));
        _deals_to_ban = _deals_to_ban.concat(faulty_returned);
        _banDeals(hotel, faulty_returned);
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    const sorted = [...available_deals].sort(DealUtils.compareByPrice);
    const best_deal = sorted[0];

    _navigation_deal = best_deal;
    best_deal.superseded = false;
    _status = STATUS.SUCCESS;

    const price_changed = best_deal.totalPrice !== _clicked_deal.totalPrice;

    const old_non_refundable = _.get(_clicked_deal, 'chooseprod.dca.nonRefundable', false);
    const new_non_refundable = _.get(best_deal, 'chooseprod.dca.nonRefundable', false);
    const raw_old_deadline = _.get(_clicked_deal, 'chooseprod.dca.dealIsFullyRefundableTill', null);
    const raw_new_deadline = _.get(best_deal, 'chooseprod.dca.dealIsFullyRefundableTill', null);

    // Treat past deadlines as effectively non-refundable so the dialog doesn't
    // claim terms changed when both states are non-refundable in practice.
    const now = Date.now();
    const is_effectively_non_refundable = (non_refundable, deadline) =>
        non_refundable || (deadline ? new Date(deadline).getTime() < now : true);
    const old_effective_deadline = is_effectively_non_refundable(old_non_refundable, raw_old_deadline)
        ? null
        : raw_old_deadline;
    const new_effective_deadline = is_effectively_non_refundable(new_non_refundable, raw_new_deadline)
        ? null
        : raw_new_deadline;
    const deadline_changed =
        deadline_display_key(old_effective_deadline) !== deadline_display_key(new_effective_deadline);

    const old_board = _clicked_deal.catering || CATERING_ROOM_ONLY;
    const new_board = best_deal.catering || CATERING_ROOM_ONLY;
    const board_changed = old_board !== new_board;

    if (price_changed || deadline_changed || board_changed) {
        _price_change_data = {
            original_deal: _clicked_deal,
            best_deal,
            old_price: _clicked_deal.totalPrice,
            new_price: best_deal.totalPrice,
            price_changed,
            old_cancellation_deadline: old_effective_deadline,
            new_cancellation_deadline: new_effective_deadline,
            deadline_changed,
            old_board,
            new_board,
            board_changed,
        };
    }
}

const LazyRecheckStore = _.extend({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit('change');
    },
    addChangeListener: function (callback) {
        this.on('change', callback);
    },
    removeChangeListener: function (callback) {
        this.removeListener('change', callback);
    },

    getStatus: function () {
        return _status;
    },
    isBusy: function () {
        return _status === STATUS.BUSY;
    },
    isLoadingDeal: function (deal_id) {
        return _status === STATUS.BUSY && _lazy_deal_ids.includes(deal_id);
    },
    getClickedDeal: function () {
        return _clicked_deal;
    },
    getNavigationDeal: function () {
        return _navigation_deal;
    },
    getPriceChangeData: function () {
        return _price_change_data;
    },
    getDealsToBan: function () {
        return _deals_to_ban;
    },
    isNoAvailability: function () {
        return _status === STATUS.NO_AVAILABILITY;
    },
    isExpired: function () {
        return _is_expired;
    },
    STATUS,
});

AppDispatcher.register(function (payload) {
    const action = payload.action;

    switch (action.actionType) {
        case HotelConstants.LAZY_RECHECK_START: {
            _reset();
            _clicked_deal = action.data.clicked_deal;
            _hotel_id = action.data.hotel_id;
            _lazy_deals = action.data.lazy_deals;
            _lazy_deal_ids = _lazy_deals.map((d) => d.id);
            _status = STATUS.BUSY;
            break;
        }

        case HotelConstants.LAZY_RECHECK_REQUESTED: {
            const { listener_id, wait_time } = action.data;
            _listener_id = listener_id;
            FirebaseManager.recheck.subscribe(
                listener_id,
                _onFirebaseData,
                _onFirebaseError,
                _onFirebaseTimeout,
                wait_time,
            );
            break;
        }

        case HotelConstants.LAZY_RECHECK_DEAL_RECEIVED: {
            const { raw_deal } = action.data;
            if (raw_deal && !_raw_recheck_deals.find((d) => d.id === raw_deal.id)) {
                _raw_recheck_deals.push(raw_deal);
            }
            if (raw_deal?.id === _clicked_deal.id) {
                _handleCompleted();
                break;
            }
            return true;
        }

        case HotelConstants.LAZY_RECHECK_COMPLETED: {
            _handleCompleted();
            break;
        }

        case HotelConstants.LAZY_RECHECK_EXPIRED: {
            _is_expired = true;
            _status = STATUS.IDLE;
            break;
        }

        case HotelConstants.LAZY_RECHECK_NO_AVAILABILITY: {
            if (_listener_id) {
                FirebaseManager.recheck.unsubscribe(_listener_id);
            }
            if (_status === STATUS.BUSY) {
                _status = STATUS.NO_AVAILABILITY;
            }

            if (_lazy_deals.length > 0) {
                const hotel = ResultsStore.getHotelById(_hotel_id);
                const deals = _lazy_deals.slice();
                setTimeout(() => deals.forEach((deal) => HotelActions.banDeal(hotel, { ...deal, hide: true })), 0);
            }
            break;
        }

        case HotelConstants.LAZY_RECHECK_CONFIRM_PRICE_CHANGE: {
            _price_change_data = null;
            break;
        }

        case HotelConstants.LAZY_RECHECK_RESET: {
            _reset();
            break;
        }

        default:
            return true;
    }

    LazyRecheckStore.emitChange();
    return true;
});

export default LazyRecheckStore;
